import { useEffect } from "react";
import { setGlobalCustomDimension } from "@leafly-com/web-utils";
import { GetServerSideProps, NextPage } from "next";
import dynamic from "next/dynamic";
import Head from "next/head";
import Link from "next/link";
import { useSelector } from "react-redux";

import getUserProfile from "api/requests/getUserProfile";
import {
  ProductReview,
  StrainReview,
  UserProfileDispensaryReview,
} from "custom-types/Reviews";
import { getUserId } from "redux/selectors/user";
import { getCountryCodeHeader } from "utils/requestContext";

import SectionHeader from "components/botanic/SectionHeader";

const StrainReviewCard = dynamic(
  () => import("components/UserProfile/ReviewCards/StrainReviewCard"),
);
const ProductReviewCard = dynamic(
  () => import("components/UserProfile/ReviewCards/ProductReviewCard"),
);
const DispensaryReviewCard = dynamic(
  () => import("components/UserProfile/ReviewCards/DispensaryReviewCard"),
);

const UUID_PATTERN =
  /[0-9a-f]{8}\b-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-\b[0-9a-f]{12}/i;

type ReviewsMetadata = {
  totalCount: number;
};

type ReviewsData = {
  data: StrainReview[] | ProductReview[] | UserProfileDispensaryReview[];
  metadata: ReviewsMetadata;
};

type Profile = {
  id: number;
  userSince: string;
  username: string;
  reviews: ReviewsData;
};

type Props = {
  profile: Profile;
};

export const getServerSideProps: GetServerSideProps = async (context) => {
  const username = context.query.username as string;
  const countryCode = getCountryCodeHeader(context.req.headers);

  if (username) {
    try {
      const { data } = await getUserProfile(username, countryCode);

      if (data) {
        if (data.publicId && !username.match(UUID_PATTERN)) {
          return {
            redirect: {
              destination: `/profile/${data.publicId}`,
              statusCode: 301,
            },
          };
        } else {
          return { props: { profile: data } };
        }
      } else {
        return {
          notFound: true,
        };
      }
    } catch (error) {
      return {
        notFound: true,
      };
    }
  } else {
    return {
      notFound: true,
    };
  }
};

const UserProfile: NextPage<Props> = ({ profile }) => {
  const userId = useSelector(getUserId);

  useEffect(() => {
    if (userId) {
      setGlobalCustomDimension("userId", userId);
    }
  }, [userId]);

  const {
    username,
    reviews: {
      data: reviews,
      metadata: { totalCount },
    },
  } = profile;

  const obscuredUsername = `${username[0]}........${
    username[username.length - 1]
  }`;
  const memberSinceYear =
    profile?.userSince &&
    new Date(profile.userSince.replace(" UTC", "")).getFullYear();

  return (
    <>
      <Head>
        <title>{`${obscuredUsername}'s Profile — Leafly`}</title>
        <meta name="robots" content="noindex, nofollow, noarchive" />
      </Head>
      <div className="container py-xxl">
        <div className="mb-xl flex justify-between">
          <div>
            <h1>{obscuredUsername}</h1>
            <span>member since {memberSinceYear}</span>
          </div>
          {userId === profile.id && (
            <div>
              <Link
                href="/me/edit-profile"
                className="block button button--small px-lg"
              >
                Edit profile
              </Link>
            </div>
          )}
        </div>
        <SectionHeader
          className="mb-lg"
          tidbit={totalCount ? `${totalCount} total` : undefined}
        >
          Recent Reviews
        </SectionHeader>
        <div className="row">
          {reviews?.length ? (
            reviews.map((review) => (
              <div
                key={`${review.reviewType}-${review.id}`}
                className="col md:col-1/2 mb-lg"
              >
                {review.reviewType === "strainReview" && (
                  <StrainReviewCard review={review} />
                )}
                {review.reviewType === "productReview" && (
                  <ProductReviewCard review={review} />
                )}
                {review.reviewType === "dispensaryReview" && (
                  <DispensaryReviewCard review={review} />
                )}
              </div>
            ))
          ) : (
            <p className="text-center my-xl">No reviews yet</p>
          )}
        </div>
      </div>
    </>
  );
};

export default UserProfile;
